import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { workersData, mockEvaluations, mockOnboardings, updateWorkerEvaluations } from '../../../mocks/workers';
import {
  Radar,
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  ResponsiveContainer,
  Legend,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip
} from 'recharts';

interface Worker {
  id: string;
  name: string;
  email: string;
  qualifications: string[];
  created_at: string;
  averageScore: number;
  rank: string;
  evaluationHistory: Array<{
    evaluatedAt: string;
    punctuality: number;
    teamwork: number;
    childCare: number;
    communication: number;
  }>;
}

interface Evaluation {
  id: string;
  worker_id: string;
  顧客目線_rating: number;
  笑顔_rating: number;
  園の方針_rating: number;
  事前準備_rating: number;
  雰囲気_rating: number;
  運資質_rating: number;
  清潔感_rating: number;
  態度_rating: number;
  work_date: string;
  overall_comment: string;
  evaluator_name: string;
  created_at: string;
}

interface Onboarding {
  id: string;
  worker_id: string;
  commute_method: string;
  created_at: string;
}

const categories = [
  { key: '顧客目線_rating', label: '顧客目線', description: '保護者・子どもの立場に立ち、ニーズを汲み取った対応ができているか' },
  { key: '笑顔_rating', label: '笑顔', description: '子どもや保護者・スタッフに常に明るく笑顔で接することができているか' },
  { key: '園の方針_rating', label: '園の方針', description: '施設のルールや保育方針を理解し、一貫して遵守できているか' },
  { key: '事前準備_rating', label: '事前準備', description: '業務前に必要な確認・準備を自発的に行えているか' },
  { key: '雰囲気_rating', label: '雰囲気', description: '職場全体の雰囲気づくりに積極的に貢献しているか' },
  { key: '運資質_rating', label: '運資質', description: 'リーダーシップや保育・運営における適性・資質があるか' },
  { key: '清潔感_rating', label: '清潔感', description: '身だしなみや衛生面において清潔さを保てているか' },
  { key: '態度_rating', label: '態度', description: '仕事への誠実な姿勢・礼儀・言葉遣いが適切であるか' }
];

const historyColors = ['#8b5cf6', '#ec4899', '#f59e0b', '#10b981', '#3b82f6', '#ef4444'];

const RatingSlider = ({
  label,
  description,
  value,
  onChange
}: {
  label: string;
  description?: string;
  value: number;
  onChange: (v: number) => void;
}) => {
  const getColor = (v: number) => {
    if (v <= 2) return '#ef4444';
    if (v <= 4) return '#f97316';
    if (v <= 6) return '#eab308';
    if (v <= 8) return '#22c55e';
    return '#6366f1';
  };

  return (
    <div className="mb-4">
      <div className="flex items-center justify-between mb-0.5">
        <span className="text-sm font-medium text-slate-700">{label}</span>
        <span className="text-base font-bold w-8 text-right" style={{ color: getColor(value) }}>
          {value}
        </span>
      </div>
      {description && (
        <p className="text-xs text-slate-400 mb-1.5 leading-relaxed">{description}</p>
      )}
      <div className="flex items-center gap-3">
        <span className="text-xs text-slate-400 w-4">1</span>
        <input
          type="range"
          min={1}
          max={10}
          step={1}
          value={value}
          onChange={(e) => onChange(parseInt(e.target.value))}
          className="flex-1 h-2 rounded-full appearance-none cursor-pointer"
          style={{
            background: `linear-gradient(to right, ${getColor(value)} 0%, ${getColor(value)} ${(value - 1) / 9 * 100}%, #e2e8f0 ${(value - 1) / 9 * 100}%, #e2e8f0 100%)`
          }}
        />
        <span className="text-xs text-slate-400 w-6">10</span>
      </div>
    </div>
  );
};

const MockWorkerDetailPage = () => {
  const { id } = useParams<{ id: string }>();
  const [worker, setWorker] = useState<Worker | null>(null);
  const [evaluations, setEvaluations] = useState<Evaluation[]>([]);
  const [onboarding, setOnboarding] = useState<Onboarding | null>(null);
  const [showAllHistory, setShowAllHistory] = useState(false);
  const [chartMode, setChartMode] = useState<'average' | 'history'>('history');
  const [showNewEvalForm, setShowNewEvalForm] = useState(false);
  const [editingEval, setEditingEval] = useState<Evaluation | null>(null);

  const [newEval, setNewEval] = useState({
    work_date: new Date().toISOString().split('T')[0],
    顧客目線_rating: 5,
    笑顔_rating: 5,
    園の方針_rating: 5,
    事前準備_rating: 5,
    雰囲気_rating: 5,
    運資質_rating: 5,
    清潔感_rating: 5,
    態度_rating: 5,
    overall_comment: '',
    evaluator_name: ''
  });

  useEffect(() => {
    if (id) {
      const foundWorker = workersData.find(w => w.id === id);
      if (foundWorker) setWorker(foundWorker);
      const workerEvals = mockEvaluations.filter(e => e.worker_id === id);
      setEvaluations(workerEvals.sort((a, b) =>
        new Date(b.work_date).getTime() - new Date(a.work_date).getTime()
      ));
      const workerOnboarding = mockOnboardings.find(o => o.worker_id === id);
      if (workerOnboarding) setOnboarding(workerOnboarding);
    }
  }, [id]);

  const handleAddEvaluation = () => {
    if (!id) return;
    updateWorkerEvaluations(id, newEval);
    const updatedWorker = workersData.find(w => w.id === id);
    if (updatedWorker) setWorker(updatedWorker);
    const workerEvals = mockEvaluations.filter(e => e.worker_id === id);
    setEvaluations(workerEvals.sort((a, b) =>
      new Date(b.work_date).getTime() - new Date(a.work_date).getTime()
    ));
    setShowNewEvalForm(false);
    setNewEval({
      work_date: new Date().toISOString().split('T')[0],
      顧客目線_rating: 5,
      笑顔_rating: 5,
      園の方針_rating: 5,
      事前準備_rating: 5,
      雰囲気_rating: 5,
      運資質_rating: 5,
      清潔感_rating: 5,
      態度_rating: 5,
      overall_comment: '',
      evaluator_name: ''
    });
  };

  const handleEditEvaluation = () => {
    if (!editingEval) return;
    const evalIndex = mockEvaluations.findIndex(e => e.id === editingEval.id);
    if (evalIndex !== -1) mockEvaluations[evalIndex] = editingEval;
    const workerEvals = mockEvaluations.filter(e => e.worker_id === id);
    setEvaluations(workerEvals.sort((a, b) =>
      new Date(b.work_date).getTime() - new Date(a.work_date).getTime()
    ));
    setEditingEval(null);
  };

  if (!worker) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 flex items-center justify-center">
        <div className="text-center">
          <div className="text-7xl mb-6">🔍</div>
          <h2 className="text-3xl font-bold text-slate-800 mb-4">ワーカーが見つかりません</h2>
          <Link to="/mock-workers" className="inline-flex items-center gap-2 px-6 py-3 bg-indigo-600 text-white text-lg rounded-lg hover:bg-indigo-700 transition-colors">
            <i className="ri-arrow-left-line"></i>
            モックワーカー一覧へ戻る
          </Link>
        </div>
      </div>
    );
  }

  const averageData = categories.map(cat => {
    const sum = evaluations.reduce((acc, ev) => acc + ((ev[cat.key as keyof Evaluation] as number) || 5), 0);
    const avg = evaluations.length > 0 ? sum / evaluations.length : 5;
    return { category: cat.label, value: parseFloat(avg.toFixed(1)) };
  });

  const historyRadarData = categories.map(cat => {
    const entry: Record<string, string | number> = { category: cat.label };
    evaluations.slice(0, 6).forEach((ev, idx) => {
      entry[`ev${idx}`] = (ev[cat.key as keyof Evaluation] as number) || 5;
    });
    return entry;
  });

  const growthData = evaluations
    .slice()
    .reverse()
    .map(ev => {
      const total = categories.reduce((sum, cat) => sum + ((ev[cat.key as keyof Evaluation] as number) || 5), 0);
      return {
        date: new Date(ev.work_date).toLocaleDateString('ja-JP', { month: 'short', day: 'numeric' }),
        score: parseFloat(total.toFixed(1))
      };
    });

  const getRankBadgeColor = (rank: string) => {
    switch (rank) {
      case 'S': return 'bg-gradient-to-r from-yellow-400 to-orange-500 text-white';
      case 'A': return 'bg-gradient-to-r from-emerald-500 to-teal-600 text-white';
      case 'B': return 'bg-gradient-to-r from-slate-400 to-slate-500 text-white';
      case 'ブロック': return 'bg-gradient-to-r from-red-500 to-red-700 text-white';
      case '未評価': return 'bg-gradient-to-r from-slate-200 to-slate-300 text-slate-600';
      default: return 'bg-slate-200 text-slate-700';
    }
  };

  const displayedEvaluations = showAllHistory ? evaluations : evaluations.slice(0, 3);

  // 評価件数が0の場合は「未評価」を返す
  const displayRank = evaluations.length === 0 ? '未評価' : worker.rank;

  // 成長/下降トレンド判定（古い順→新しい順で比較）
  const getTrendInfo = () => {
    if (evaluations.length < 2) return null;
    const sorted = [...evaluations].reverse(); // 古い順
    const scores = sorted.map(ev =>
      categories.reduce((sum, cat) => sum + ((ev[cat.key as keyof Evaluation] as number) || 5) * 2, 0)
    );
    const first = scores[0];
    const last = scores[scores.length - 1];
    if (last > first + 2) return 'growth';
    if (last < first - 2) return 'decline';
    return null;
  };

  const trendInfo = getTrendInfo();
  const trendColor = trendInfo === 'growth' ? '#10b981' : trendInfo === 'decline' ? '#ef4444' : '#6366f1';

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50">
      {/* ヘッダー */}
      <div className="bg-white border-b border-slate-200 sticky top-0 z-10 shadow-sm">
        <div className="max-w-7xl mx-auto px-3 sm:px-6 py-3 sm:py-4">
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-2 sm:gap-4 min-w-0">
              <Link
                to="/mock-workers"
                className="flex items-center gap-1 sm:gap-2 px-2 sm:px-4 py-2 text-slate-600 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition-all text-sm sm:text-base whitespace-nowrap"
              >
                <i className="ri-arrow-left-line text-lg sm:text-xl"></i>
                <span className="hidden sm:inline">モックワーカー一覧へ戻る</span>
                <span className="sm:hidden">一覧</span>
              </Link>
              <div className="h-6 w-px bg-slate-300 hidden sm:block"></div>
              <h1 className="text-base sm:text-2xl font-bold text-slate-800 truncate hidden sm:block">ワーカー詳細（モック）</h1>
            </div>
            {evaluations.length > 0 && (
              <Link
                to={`/mock-workers/${id}/offer`}
                className="flex items-center gap-1 sm:gap-2 px-3 sm:px-6 py-2 sm:py-3 bg-gradient-to-r from-indigo-600 to-purple-600 text-white text-xs sm:text-base font-semibold rounded-lg hover:from-indigo-700 hover:to-purple-700 transition-all shadow-lg hover:shadow-xl whitespace-nowrap"
              >
                <i className="ri-mail-send-line text-base sm:text-xl"></i>
                <span className="hidden sm:inline">オファーメールを送信</span>
                <span className="sm:hidden">オファー</span>
              </Link>
            )}
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-3 sm:px-6 py-4 sm:py-8">
        {/* ワーカー基本情報 */}
        <div className="bg-white rounded-2xl shadow-lg p-4 sm:p-8 mb-4 sm:mb-8 border border-slate-200">
          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between mb-4 sm:mb-6 gap-4">
            <div className="flex items-center gap-3 sm:gap-6">
              <div className="w-14 h-14 sm:w-24 sm:h-24 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-2xl flex items-center justify-center text-white text-2xl sm:text-4xl font-bold shadow-lg flex-shrink-0">
                {worker.name.charAt(0)}
              </div>
              <div className="min-w-0">
                <h2 className="text-xl sm:text-3xl font-bold text-slate-800 mb-1 sm:mb-4">{worker.name}</h2>
                <p className="text-sm sm:text-lg text-slate-600 mb-2 sm:mb-3 truncate">{worker.email}</p>
                <div className="flex flex-wrap items-center gap-1.5 sm:gap-3">
                  {worker.qualifications.map((qual, idx) => (
                    <span key={idx} className="px-2 sm:px-4 py-1 sm:py-1.5 bg-gradient-to-r from-emerald-500 to-teal-600 text-white text-xs sm:text-base rounded-full font-medium shadow-md">
                      {qual}
                    </span>
                  ))}
                </div>
              </div>
            </div>
            <div className={`px-4 sm:px-6 py-2 sm:py-3 rounded-xl text-lg sm:text-2xl font-bold shadow-lg self-start ${getRankBadgeColor(displayRank)}`}>
              {displayRank}ランク
            </div>
          </div>
          <div className="grid grid-cols-3 gap-2 sm:gap-6 pt-4 sm:pt-6 border-t border-slate-200">
            <div className="text-center">
              <div className="text-xs sm:text-base text-slate-600 mb-1 sm:mb-2">通勤手段</div>
              <div className="text-sm sm:text-xl font-bold text-slate-800">{onboarding?.commute_method || '未登録'}</div>
            </div>
            <div className="text-center">
              <div className="text-xs sm:text-base text-slate-600 mb-1 sm:mb-2">登録日</div>
              <div className="text-sm sm:text-xl font-bold text-slate-800">{new Date(worker.created_at).toLocaleDateString('ja-JP')}</div>
            </div>
            <div className="text-center">
              <div className="text-xs sm:text-base text-slate-600 mb-1 sm:mb-2">評価回数</div>
              <div className="text-sm sm:text-xl font-bold text-slate-800">{evaluations.length}回</div>
            </div>
          </div>
        </div>

        {/* 平均スコアとランク判定 */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-8 mb-4 sm:mb-8">
          <div className="bg-white rounded-2xl shadow-lg p-5 sm:p-8 border border-slate-200">
            <h3 className="text-base sm:text-xl font-bold text-slate-800 mb-4 sm:mb-6 flex items-center gap-2 sm:gap-3">
              <i className="ri-bar-chart-box-line text-indigo-600 text-xl sm:text-2xl"></i>
              <span className="hidden sm:inline">平均スコア</span>
              <span className="sm:hidden">平均</span>
            </h3>
            <div className="text-center">
              <div className="text-5xl sm:text-6xl font-bold text-indigo-600 mb-2 sm:mb-3">{worker.averageScore.toFixed(1)}</div>
              <div className="text-sm sm:text-lg text-slate-600">/ 80.0点</div>
            </div>
          </div>
          <div className="bg-white rounded-2xl shadow-lg p-5 sm:p-8 border border-slate-200">
            <h3 className="text-base sm:text-xl font-bold text-slate-800 mb-4 sm:mb-6 flex items-center gap-2 sm:gap-3">
              <i className="ri-trophy-line text-indigo-600 text-xl sm:text-2xl"></i>
              <span className="hidden sm:inline">ランク基準</span>
              <span className="sm:hidden">ランク</span>
            </h3>
            <div className="space-y-2 sm:space-y-3">
              <div className="flex items-start justify-between p-2.5 sm:p-3 bg-gradient-to-r from-yellow-50 to-orange-50 rounded-lg border border-yellow-200">
                <div>
                  <span className="font-bold text-yellow-700 text-sm sm:text-base block">Sランク</span>
                  <span className="text-xs text-yellow-600 mt-0.5 block">即直接雇用・最適な人材幹部候補</span>
                </div>
                <span className="text-slate-700 text-sm sm:text-base font-semibold whitespace-nowrap ml-2 mt-0.5">60点以上</span>
              </div>
              <div className="flex items-start justify-between p-2.5 sm:p-3 bg-gradient-to-r from-emerald-50 to-teal-50 rounded-lg border border-emerald-200">
                <div>
                  <span className="font-bold text-emerald-700 text-sm sm:text-base block">Aランク</span>
                  <span className="text-xs text-emerald-600 mt-0.5 block">直接スポットへ招待</span>
                </div>
                <span className="text-slate-700 text-sm sm:text-base font-semibold whitespace-nowrap ml-2 mt-0.5">50点以上</span>
              </div>
              <div className="flex items-start justify-between p-2.5 sm:p-3 bg-slate-50 rounded-lg border border-slate-200">
                <div>
                  <span className="font-bold text-slate-700 text-sm sm:text-base block">Bランク</span>
                  <span className="text-xs text-slate-500 mt-0.5 block">タイムリー継続</span>
                </div>
                <span className="text-slate-700 text-sm sm:text-base font-semibold whitespace-nowrap ml-2 mt-0.5">40点以上</span>
              </div>
              <div className="flex items-start justify-between p-2.5 sm:p-3 bg-gradient-to-r from-red-50 to-rose-50 rounded-lg border border-red-200">
                <div>
                  <span className="font-bold text-red-700 text-sm sm:text-base block">ブロック</span>
                  <span className="text-xs text-red-500 mt-0.5 block">オファー不可・要注意</span>
                </div>
                <span className="text-slate-700 text-sm sm:text-base font-semibold whitespace-nowrap ml-2 mt-0.5">39点以下</span>
              </div>
            </div>
          </div>
        </div>

        {/* レーダーチャートと成長推移 */}
        {evaluations.length > 0 && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-8 mb-4 sm:mb-8">
            {/* レーダーチャート */}
            <div className="bg-white rounded-2xl shadow-lg p-5 sm:p-8 border border-slate-200">
              <div className="flex items-center justify-between mb-4 sm:mb-6">
                <h3 className="text-base sm:text-xl font-bold text-slate-800 flex items-center gap-2 sm:gap-3">
                  <i className="ri-radar-line text-indigo-600 text-xl sm:text-2xl"></i>
                  <span className="hidden sm:inline">評価レーダーチャート</span>
                  <span className="sm:hidden">レーダー</span>
                </h3>
                <div className="flex gap-1.5 sm:gap-2">
                  <button
                    onClick={() => setChartMode('average')}
                    className={`px-2.5 sm:px-4 py-1.5 sm:py-2 rounded-lg text-xs sm:text-sm font-medium transition-all whitespace-nowrap ${chartMode === 'average' ? 'bg-indigo-600 text-white shadow-md' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}
                  >
                    平均
                  </button>
                  <button
                    onClick={() => setChartMode('history')}
                    className={`px-2.5 sm:px-4 py-1.5 sm:py-2 rounded-lg text-xs sm:text-sm font-medium transition-all whitespace-nowrap ${chartMode === 'history' ? 'bg-indigo-600 text-white shadow-md' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}
                  >
                    履歴重ね
                  </button>
                </div>
              </div>

              {chartMode === 'average' ? (
                <ResponsiveContainer width="100%" height={280}>
                  <RadarChart data={averageData}>
                    <PolarGrid stroke="#e2e8f0" />
                    <PolarAngleAxis dataKey="category" tick={{ fill: '#475569', fontSize: 11 }} />
                    <PolarRadiusAxis angle={90} domain={[0, 10]} tick={{ fill: '#64748b', fontSize: 10 }} />
                    <Radar name="平均" dataKey="value" stroke="#6366f1" fill="#6366f1" fillOpacity={0.55} />
                    <Legend wrapperStyle={{ fontSize: '12px' }} />
                  </RadarChart>
                </ResponsiveContainer>
              ) : (
                <>
                  <ResponsiveContainer width="100%" height={240}>
                    <RadarChart data={historyRadarData}>
                      <PolarGrid stroke="#e2e8f0" />
                      <PolarAngleAxis dataKey="category" tick={{ fill: '#475569', fontSize: 11 }} />
                      <PolarRadiusAxis angle={90} domain={[0, 10]} tick={{ fill: '#64748b', fontSize: 10 }} />
                      {evaluations.slice(0, 6).map((ev, idx) => {
                        const color = historyColors[idx % historyColors.length];
                        return (
                          <Radar
                            key={ev.id}
                            legendType="none"
                            dataKey={`ev${idx}`}
                            stroke={color}
                            fill={color}
                            fillOpacity={0.18}
                            strokeWidth={2}
                          />
                        );
                      })}
                    </RadarChart>
                  </ResponsiveContainer>
                  <div className="mt-2 flex flex-wrap gap-1.5 sm:gap-2 justify-center">
                    {evaluations.slice(0, 6).map((ev, idx) => (
                      <span
                        key={ev.id}
                        className="flex items-center gap-1 sm:gap-1.5 text-xs px-2 sm:px-2.5 py-1 rounded-full"
                        style={{
                          backgroundColor: `${historyColors[idx % historyColors.length]}20`,
                          color: historyColors[idx % historyColors.length],
                          border: `1px solid ${historyColors[idx % historyColors.length]}60`
                        }}
                      >
                        <span
                          className="w-2 h-2 rounded-full inline-block flex-shrink-0"
                          style={{ backgroundColor: historyColors[idx % historyColors.length] }}
                        ></span>
                        {new Date(ev.work_date).toLocaleDateString('ja-JP', { month: 'short', day: 'numeric' })}
                      </span>
                    ))}
                  </div>
                </>
              )}
            </div>

            {/* 成長推移グラフ */}
            <div className="bg-white rounded-2xl shadow-lg p-5 sm:p-8 border border-slate-200">
              <h3 className="text-base sm:text-xl font-bold text-slate-800 mb-4 sm:mb-6 flex items-center gap-2 sm:gap-3 flex-wrap">
                <i className="ri-line-chart-line text-indigo-600 text-xl sm:text-2xl"></i>
                <span className="hidden sm:inline">成長推移</span>
                <span className="sm:hidden">成長</span>
                {trendInfo === 'growth' && (
                  <span className="inline-flex items-center gap-1.5 px-2.5 sm:px-3 py-1 rounded-full bg-emerald-100 text-emerald-700 text-xs sm:text-sm font-semibold">
                    <span className="relative flex h-2.5 w-2.5 sm:h-3 sm:w-3">
                      <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                      <span className="relative inline-flex rounded-full h-2.5 w-2.5 sm:h-3 sm:w-3 bg-emerald-500"></span>
                    </span>
                    <span className="animate-bounce inline-block">↑</span>
                    <span className="hidden sm:inline">成長中</span>
                    <span className="sm:hidden">成長</span>
                  </span>
                )}
                {trendInfo === 'decline' && (
                  <span className="inline-flex items-center gap-1.5 px-2.5 sm:px-3 py-1 rounded-full bg-red-100 text-red-600 text-xs sm:text-sm font-semibold">
                    <span className="relative flex h-2.5 w-2.5 sm:h-3 sm:w-3">
                      <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75"></span>
                      <span className="relative inline-flex rounded-full h-2.5 w-2.5 sm:h-3 sm:w-3 bg-red-500"></span>
                    </span>
                    <span className="animate-bounce inline-block">↓</span>
                    <span className="hidden sm:inline">下降中</span>
                    <span className="sm:hidden">下降</span>
                  </span>
                )}
              </h3>
              <ResponsiveContainer width="100%" height={280}>
                <LineChart data={growthData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="date" tick={{ fill: '#64748b', fontSize: 11 }} />
                  <YAxis domain={[0, 80]} tick={{ fill: '#64748b', fontSize: 11 }} />
                  <RechartsTooltip
                    contentStyle={{
                      backgroundColor: '#fff',
                      border: '1px solid #e2e8f0',
                      borderRadius: '8px',
                      fontSize: '13px'
                    }}
                  />
                  <Line
                    type="monotone"
                    dataKey="score"
                    stroke={trendColor}
                    strokeWidth={3}
                    dot={{ fill: trendColor, r: 4 }}
                    activeDot={{ r: 6 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}

        {/* 新規評価追加ボタン */}
        <div className="mb-4 sm:mb-8">
          <button
            onClick={() => setShowNewEvalForm(!showNewEvalForm)}
            className="w-full flex items-center justify-center gap-2 sm:gap-3 px-4 sm:px-6 py-3 sm:py-4 bg-gradient-to-r from-emerald-500 to-teal-600 text-white text-base sm:text-lg font-semibold rounded-xl hover:from-emerald-600 hover:to-teal-700 transition-all shadow-lg hover:shadow-xl whitespace-nowrap"
          >
            <i className={`text-xl sm:text-2xl ${showNewEvalForm ? 'ri-close-line' : 'ri-add-line'}`}></i>
            {showNewEvalForm ? '評価フォームを閉じる' : '新しい評価を追加'}
          </button>
        </div>

        {/* 新規評価フォーム */}
        {showNewEvalForm && (
          <div className="bg-white rounded-2xl shadow-lg p-4 sm:p-8 mb-4 sm:mb-8 border border-slate-200">
            <h3 className="text-lg sm:text-2xl font-bold text-slate-800 mb-4 sm:mb-6 flex items-center gap-2 sm:gap-3">
              <i className="ri-file-add-line text-emerald-600 text-xl sm:text-2xl"></i>
              <span className="hidden sm:inline">新規評価を追加</span>
              <span className="sm:hidden">新規</span>
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6 mb-6 sm:mb-8">
              <div>
                <label className="block text-sm sm:text-base font-medium text-slate-700 mb-1.5 sm:mb-2">勤務日</label>
                <input
                  type="date"
                  value={newEval.work_date}
                  onChange={(e) => setNewEval({ ...newEval, work_date: e.target.value })}
                  className="w-full px-3 sm:px-4 py-2.5 sm:py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-sm sm:text-base"
                />
              </div>
              <div>
                <label className="block text-sm sm:text-base font-medium text-slate-700 mb-1.5 sm:mb-2">評価者名</label>
                <input
                  type="text"
                  value={newEval.evaluator_name}
                  onChange={(e) => setNewEval({ ...newEval, evaluator_name: e.target.value })}
                  placeholder="例: 田村 美咲"
                  className="w-full px-3 sm:px-4 py-2.5 sm:py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-sm sm:text-base"
                />
              </div>
            </div>
            <div className="bg-slate-50 rounded-xl p-4 sm:p-6 mb-4 sm:mb-6 border border-slate-200">
              <div className="text-sm sm:text-base font-semibold text-slate-700 mb-3 sm:mb-4 flex items-center gap-2">
                <i className="ri-sliders-line text-indigo-600"></i>
                各項目の評価（1〜10）
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 sm:gap-x-10 gap-y-1 sm:gap-y-2">
                {categories.map(cat => (
                  <RatingSlider
                    key={cat.key}
                    label={cat.label}
                    description={cat.description}
                    value={newEval[cat.key as keyof typeof newEval] as number}
                    onChange={(v) => setNewEval({ ...newEval, [cat.key]: v })}
                  />
                ))}
              </div>
            </div>
            <div className="mb-4 sm:mb-6">
              <label className="block text-sm sm:text-base font-medium text-slate-700 mb-1.5 sm:mb-2">総合コメント</label>
              <textarea
                value={newEval.overall_comment}
                onChange={(e) => setNewEval({ ...newEval, overall_comment: e.target.value })}
                placeholder="総合的な評価コメントを入力してください"
                rows={4}
                className="w-full px-3 sm:px-4 py-2.5 sm:py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-sm sm:text-base"
              />
            </div>
            <button
              onClick={handleAddEvaluation}
              className="w-full flex items-center justify-center gap-2 sm:gap-3 px-4 sm:px-6 py-3 sm:py-4 bg-gradient-to-r from-emerald-500 to-teal-600 text-white text-base sm:text-lg font-semibold rounded-xl hover:from-emerald-600 hover:to-teal-700 transition-all shadow-lg hover:shadow-xl whitespace-nowrap"
            >
              <i className="ri-save-line text-xl sm:text-2xl"></i>
              <span className="hidden sm:inline">評価を保存</span>
              <span className="sm:hidden">保存</span>
            </button>
          </div>
        )}

        {/* 評価履歴 */}
        <div className="bg-white rounded-2xl shadow-lg p-4 sm:p-8 border border-slate-200">
          <h3 className="text-lg sm:text-2xl font-bold text-slate-800 mb-4 sm:mb-6 flex items-center gap-2 sm:gap-3">
            <i className="ri-history-line text-indigo-600 text-xl sm:text-2xl"></i>
            <span className="hidden sm:inline">評価履歴</span>
            <span className="sm:hidden">履歴</span>
            （{evaluations.length}件）
          </h3>
          {evaluations.length === 0 ? (
            <div className="text-center py-10 sm:py-12">
              <div className="text-5xl sm:text-6xl mb-4">📋</div>
              <p className="text-base sm:text-xl text-slate-600">まだ評価がありません</p>
            </div>
          ) : (
            <>
              <div className="space-y-4 sm:space-y-6">
                {displayedEvaluations.map((ev) => {
                  return (
                    <div key={ev.id} className="border border-slate-200 rounded-xl p-4 sm:p-6 hover:shadow-md transition-shadow">
                      <div className="flex items-start justify-between mb-3 sm:mb-4">
                        <div>
                          <div className="text-sm sm:text-lg font-bold text-slate-800 mb-0.5 sm:mb-1">
                            {new Date(ev.work_date).toLocaleDateString('ja-JP', { year: 'numeric', month: 'long', day: 'numeric' })}
                          </div>
                          <div className="text-xs sm:text-base text-slate-600">評価者: {ev.evaluator_name}</div>
                        </div>
                        <button
                          onClick={() => setEditingEval(ev)}
                          className="flex items-center gap-1 sm:gap-2 px-2.5 sm:px-4 py-1.5 sm:py-2 bg-slate-100 text-slate-700 text-xs sm:text-sm rounded-lg hover:bg-slate-200 transition-colors whitespace-nowrap"
                        >
                          <i className="ri-edit-line"></i>
                          編集
                        </button>
                      </div>
                      {/* モバイル：縦並び、PC：横並び */}
                      <div className="flex flex-col sm:grid sm:grid-cols-2 gap-4 sm:gap-6">
                        <div>
                          {(() => {
                            const evalIndex = evaluations.findIndex(e => e.id === ev.id);
                            const color = historyColors[evalIndex % historyColors.length];
                            const evalData = categories.map(cat => ({
                              category: cat.label,
                              value: ((ev[cat.key as keyof Evaluation] as number) || 5) * 2
                            }));
                            return (
                              <ResponsiveContainer width="100%" height={220}>
                                <RadarChart data={evalData}>
                                  <PolarGrid stroke="#e2e8f0" />
                                  <PolarAngleAxis dataKey="category" tick={{ fill: '#475569', fontSize: 10 }} />
                                  <PolarRadiusAxis angle={90} domain={[0, 10]} tick={{ fill: '#64748b', fontSize: 9 }} />
                                  <Radar dataKey="value" stroke={color} fill={color} fillOpacity={0.45} strokeWidth={2} />
                                </RadarChart>
                              </ResponsiveContainer>
                            );
                          })()}
                        </div>
                        <div>
                          <div className="grid grid-cols-2 gap-2 sm:gap-3 mb-3 sm:mb-4">
                            {categories.map(cat => (
                              <div key={cat.key} className="flex items-center justify-between p-2 sm:p-3 bg-slate-50 rounded-lg">
                                <span className="text-xs sm:text-sm text-slate-700">{cat.label}</span>
                                <span className="text-sm sm:text-base font-bold text-indigo-600">{ev[cat.key as keyof Evaluation]}/10</span>
                              </div>
                            ))}
                          </div>
                          {ev.overall_comment && (
                            <div className="p-3 sm:p-4 bg-indigo-50 rounded-lg border border-indigo-200">
                              <div className="text-xs sm:text-sm font-medium text-indigo-900 mb-1 sm:mb-2">総合コメント</div>
                              <p className="text-sm sm:text-base text-indigo-800">{ev.overall_comment}</p>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
              {evaluations.length > 3 && (
                <button
                  onClick={() => setShowAllHistory(!showAllHistory)}
                  className="w-full mt-4 sm:mt-6 flex items-center justify-center gap-2 px-4 sm:px-6 py-2.5 sm:py-3 bg-slate-100 text-slate-700 text-sm sm:text-base font-medium rounded-lg hover:bg-slate-200 transition-colors whitespace-nowrap"
                >
                  {showAllHistory ? (
                    <><i className="ri-arrow-up-line"></i>一部のみ表示</>
                  ) : (
                    <><i className="ri-arrow-down-line"></i>すべて表示（{evaluations.length}件）</>
                  )}
                </button>
              )}
            </>
          )}
        </div>
      </div>

      {/* 編集モーダル */}
      {editingEval && (
        <div className="fixed inset-0 bg-black/50 flex items-end sm:items-center justify-center z-50 p-0 sm:p-6">
          <div className="bg-white rounded-t-2xl sm:rounded-2xl shadow-2xl w-full sm:max-w-3xl max-h-[92vh] sm:max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-slate-200 px-4 sm:px-8 py-4 sm:py-6 flex items-center justify-between">
              <h3 className="text-lg sm:text-2xl font-bold text-slate-800 flex items-center gap-2 sm:gap-3">
                <i className="ri-edit-line text-indigo-600 text-xl sm:text-2xl"></i>
                <span className="hidden sm:inline">評価を編集</span>
                <span className="sm:hidden">編集</span>
              </h3>
              <button
                onClick={() => setEditingEval(null)}
                className="w-9 h-9 sm:w-10 sm:h-10 flex items-center justify-center text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
              >
                <i className="ri-close-line text-xl sm:text-2xl"></i>
              </button>
            </div>
            <div className="p-4 sm:p-8">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6 mb-6 sm:mb-8">
                <div>
                  <label className="block text-sm sm:text-base font-medium text-slate-700 mb-1.5 sm:mb-2">勤務日</label>
                  <input
                    type="date"
                    value={editingEval.work_date}
                    onChange={(e) => setEditingEval({ ...editingEval, work_date: e.target.value })}
                    className="w-full px-3 sm:px-4 py-2.5 sm:py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-sm sm:text-base"
                  />
                </div>
                <div>
                  <label className="block text-sm sm:text-base font-medium text-slate-700 mb-1.5 sm:mb-2">評価者名</label>
                  <input
                    type="text"
                    value={editingEval.evaluator_name}
                    onChange={(e) => setEditingEval({ ...editingEval, evaluator_name: e.target.value })}
                    className="w-full px-3 sm:px-4 py-2.5 sm:py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-sm sm:text-base"
                  />
                </div>
              </div>
              <div className="bg-slate-50 rounded-xl p-4 sm:p-6 mb-4 sm:mb-6 border border-slate-200">
                <div className="text-sm sm:text-base font-semibold text-slate-700 mb-3 sm:mb-4 flex items-center gap-2">
                  <i className="ri-sliders-line text-indigo-600"></i>
                  各項目の評価（1〜10）
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 sm:gap-x-10 gap-y-1 sm:gap-y-2">
                  {categories.map(cat => (
                    <RatingSlider
                      key={cat.key}
                      label={cat.label}
                      description={cat.description}
                      value={editingEval[cat.key as keyof Evaluation] as number}
                      onChange={(v) => setEditingEval({ ...editingEval, [cat.key]: v })}
                    />
                  ))}
                </div>
              </div>
              <div className="mb-4 sm:mb-6">
                <label className="block text-sm sm:text-base font-medium text-slate-700 mb-1.5 sm:mb-2">総合コメント</label>
                <textarea
                  value={editingEval.overall_comment}
                  onChange={(e) => setEditingEval({ ...editingEval, overall_comment: e.target.value })}
                  rows={4}
                  className="w-full px-3 sm:px-4 py-2.5 sm:py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-sm sm:text-base"
                />
              </div>
              <div className="flex gap-3 sm:gap-4">
                <button
                  onClick={() => setEditingEval(null)}
                  className="flex-1 px-4 sm:px-6 py-2.5 sm:py-3 bg-slate-100 text-slate-700 text-sm sm:text-base font-medium rounded-lg hover:bg-slate-200 transition-colors whitespace-nowrap"
                >
                  キャンセル
                </button>
                <button
                  onClick={handleEditEvaluation}
                  className="flex-1 flex items-center justify-center gap-2 px-4 sm:px-6 py-2.5 sm:py-3 bg-gradient-to-r from-indigo-600 to-purple-600 text-white text-sm sm:text-base font-semibold rounded-lg hover:from-indigo-700 hover:to-purple-700 transition-all shadow-lg hover:shadow-xl whitespace-nowrap"
                >
                  <i className="ri-save-line text-lg sm:text-xl"></i>
                  <span className="hidden sm:inline">保存</span>
                  <span className="sm:hidden">保存</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MockWorkerDetailPage;
